import { BackendSource, isSourceAccessible } from ".";
import { NewsFeedItem, SOURCE_TIME_FIELDS } from "./models";
import useFirehoseStore from "@/stores/FirehoseStore";
import {
  firehoseFilterMatcher,
  firehosePortfolioMatcher,
} from "@/widget-components/firehose-widgets/firehose-all/utils";
import { firstMatchingView } from "@/shared/utils/savedViewHighlight";
import type { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import { playNotificationSound } from "@/utils/sound-utils";
import {
  getNotificationPermission,
  showBrowserNotification,
} from "@/utils/browser-notification-utils";
import { alertTitle, matchingAlertLists } from "@/shared/utils/alertLists";
import {
  ChannelSound,
  DEFAULT_NOTIFICATION_SOUND_SETTINGS,
} from "@/utils/notificationSounds";
import type { Portfolio } from "@/types/Portfolio";
import {
  DEFAULT_TRUTH_SOCIAL_NOTIFICATION_SETTINGS,
  passesTruthSocialContentRules,
  TruthSocialNotificationSettings,
} from "./truthSocialNotificationRules";

const STALENESS_THRESHOLD_MS = 5 * 60 * 1000;

const STOCKS_NEWS_SOURCES: BackendSource[] = [
  "stocks-one",
  "stocks-two",
  "stocks-three",
  "stocks-four",
  "stocks-four-filings",
];

type PortfoliosBySourceMap = Partial<Record<string, Portfolio[]>>;
// Combined watchlists with the same alert method on, matched as rules — see
// usePortfoliosWithSoundEnabled.ts.
type CombinedBySourceMap = Partial<Record<string, CategoryQuery[]>>;

let authContext: { roles: string[]; isInternalUser: boolean } = {
  roles: [],
  isInternalUser: false,
};

let soundContext: {
  portfoliosWithSoundEnabled: PortfoliosBySourceMap | null;
  combinedWithSoundEnabled?: CombinedBySourceMap | null;
  notificationsSoundTruthSocial: boolean;
  truthSocialNotify: TruthSocialNotificationSettings;
  notificationSounds: { truthSocial: ChannelSound; portfolio: ChannelSound };
} = {
  portfoliosWithSoundEnabled: null,
  combinedWithSoundEnabled: null,
  notificationsSoundTruthSocial: false,
  truthSocialNotify: DEFAULT_TRUTH_SOCIAL_NOTIFICATION_SETTINGS,
  notificationSounds: {
    truthSocial: DEFAULT_NOTIFICATION_SOUND_SETTINGS.truthSocial,
    portfolio: DEFAULT_NOTIFICATION_SOUND_SETTINGS.portfolio,
  },
};

// Browser (OS) pop-up notification context. Parallels soundContext so the
// outside-React ingest path can read current toggles + popup-enabled watchlists.
// Permission itself is read live inside showBrowserNotification, not synced here.
let browserNotifyContext: {
  enabled: boolean;
  truthSocial: boolean;
  portfoliosWithPopupEnabled: PortfoliosBySourceMap | null;
  combinedWithPopupEnabled?: CombinedBySourceMap | null;
} = {
  enabled: false,
  truthSocial: false,
  portfoliosWithPopupEnabled: null,
  combinedWithPopupEnabled: null,
};

// Timestamps of recent Truth Social beeps, used by the burst throttle. Lives
// outside React alongside the rest of the sound context.
let truthSocialBeepTimes: number[] = [];

export function setFirehoseAuthContext(ctx: {
  roles: string[];
  isInternalUser: boolean;
}) {
  authContext = ctx;
}

export function setFirehoseSoundContext(ctx: {
  portfoliosWithSoundEnabled: PortfoliosBySourceMap | null;
  combinedWithSoundEnabled?: CombinedBySourceMap | null;
  notificationsSoundTruthSocial: boolean;
  truthSocialNotify: TruthSocialNotificationSettings;
  notificationSounds: { truthSocial: ChannelSound; portfolio: ChannelSound };
}) {
  soundContext = ctx;
}

export function setFirehoseBrowserNotifyContext(ctx: {
  enabled: boolean;
  truthSocial: boolean;
  portfoliosWithPopupEnabled: PortfoliosBySourceMap | null;
  combinedWithPopupEnabled?: CombinedBySourceMap | null;
}) {
  browserNotifyContext = ctx;
}

/**
 * Decides whether a Truth Social story should play a sound, applying the
 * content-type skips, keyword allowlist, then the burst throttle. Only truths
 * that pass the content rules consume throttle budget.
 */
function shouldBeepTruthSocial(title: string): boolean {
  const settings = soundContext.truthSocialNotify;

  if (!passesTruthSocialContentRules(title, settings)) return false;

  if (settings.throttleEnabled) {
    const now = Date.now();
    const windowMs = settings.cooldownMinutes * 60 * 1000;
    truthSocialBeepTimes = truthSocialBeepTimes.filter(
      (t) => now - t < windowMs
    );
    if (truthSocialBeepTimes.length >= settings.burstCount) return false;
    truthSocialBeepTimes.push(now);
  }

  return true;
}

export function normalizeSourceTime(story: NewsFeedItem): void {
  const sourceTimeField = SOURCE_TIME_FIELDS[story.backend_source];
  if (sourceTimeField && (story as any)[sourceTimeField]) {
    story.source_published_time = (story as any)[sourceTimeField];
  }
}

/**
 * Assembles the watchlists to match a story against: the story's own source +
 * the "allStories" bucket, plus the synthetic "stocks-news" bucket for the
 * stocks-* sources. Shared by the sound and pop-up paths.
 */
function collectPortfoliosForStory<T>(
  map: Partial<Record<string, T[]>> | null | undefined,
  source: BackendSource,
  isStocksNews: boolean
): T[] {
  const base = [...(map?.[source] ?? []), ...(map?.allStories ?? [])];
  return isStocksNews ? [...base, ...(map?.["stocks-news"] ?? [])] : base;
}

export interface IngestOptions {
  /** Skip the sound-notification path — used by the polling fallback path. */
  skipSound?: boolean;
}

/**
 * Single ingestion path used by both the live SSE handler and the polling
 * fallback. Idempotent: reads current store state and skips already-known ids
 * so repeated polls don't duplicate stocks-news entries (addTypeStory itself
 * does not dedupe).
 */
export function ingestFirehoseStory(
  newStory: NewsFeedItem,
  opts: IngestOptions = {}
) {
  const store = useFirehoseStore.getState();
  if (store.allStories.some((s) => s.id === newStory.id)) {
    return;
  }

  if (
    !isSourceAccessible(
      newStory.backend_source,
      authContext.roles,
      authContext.isInternalUser
    )
  ) {
    return;
  }

  newStory.isNew = true;
  newStory.received_at = Date.now();
  normalizeSourceTime(newStory);

  store.addStory(newStory);

  const isStocksNews = STOCKS_NEWS_SOURCES.includes(newStory.backend_source);
  if (isStocksNews) {
    store.addTypeStory(newStory, ["stocks-news"]);
  }

  if (opts.skipSound) return;

  const isStale =
    newStory.source_published_time != null &&
    Date.now() - new Date(newStory.source_published_time).getTime() >
      STALENESS_THRESHOLD_MS;

  if (isStale) return;

  const isTruthSocial = newStory.backend_source === "truth-social";

  // Sound (unchanged behaviour): match against sound-enabled watchlists.
  const soundPortfolios = collectPortfoliosForStory(
    soundContext.portfoliosWithSoundEnabled,
    newStory.backend_source,
    isStocksNews
  );
  const matchedSoundPortfolio = firehosePortfolioMatcher(
    newStory,
    soundPortfolios
  );
  // Combined watchlists are matched as their rule, AND and OR intact.
  const matchedSoundCombined =
    !matchedSoundPortfolio &&
    firstMatchingView(
      newStory,
      collectPortfoliosForStory(
        soundContext.combinedWithSoundEnabled,
        newStory.backend_source,
        isStocksNews
      ),
      firehoseFilterMatcher
    );

  // Browser pop-up: gated by the master toggle + live permission, matched
  // against popup-enabled lists (independent of the sound channel). It names
  // every matching list: one story raises one pop-up for the whole page, so
  // no widget's order can pick one.
  const browserOn =
    browserNotifyContext.enabled && getNotificationPermission() === "granted";
  const listAlert = browserOn
    ? alertTitle(
        matchingAlertLists(
          newStory,
          collectPortfoliosForStory(
            browserNotifyContext.portfoliosWithPopupEnabled,
            newStory.backend_source,
            isStocksNews
          ),
          collectPortfoliosForStory(
            browserNotifyContext.combinedWithPopupEnabled,
            newStory.backend_source,
            isStocksNews
          ),
          firehosePortfolioMatcher,
          firehoseFilterMatcher
        )
      )
    : undefined;

  // Truth Social gate computed once so the burst throttle isn't consumed twice,
  // and only when a TS sound or pop-up actually wants it.
  const wantsTruthSocial =
    isTruthSocial &&
    (soundContext.notificationsSoundTruthSocial ||
      (browserOn && browserNotifyContext.truthSocial));
  const truthSocialPasses =
    wantsTruthSocial && shouldBeepTruthSocial(newStory.title);

  // Dispatch sound.
  if (matchedSoundPortfolio || matchedSoundCombined) {
    playNotificationSound(soundContext.notificationSounds.portfolio);
  } else if (truthSocialPasses && soundContext.notificationsSoundTruthSocial) {
    playNotificationSound(soundContext.notificationSounds.truthSocial);
  }

  // Dispatch browser pop-up (independent of sound).
  if (browserOn) {
    if (listAlert) {
      showBrowserNotification({
        title: listAlert.title,
        body: newStory.title,
        tag: listAlert.tag,
      });
    } else if (truthSocialPasses && browserNotifyContext.truthSocial) {
      showBrowserNotification({
        title: "Truth Social",
        body: newStory.title,
        tag: "truth-social",
      });
    }
  }
}
